/**
 * Shared browser server-state key registry for sealed story routes.
 *
 * Every key starts with one root and places the owning domain before identity.
 * That makes cross-route invalidation explicit without relying on endpoint
 * strings or component-local caches. Keep key segments immutable and scalar.
 */

import { timberPriceQueryKeys } from "./timberPriceQueryKeys";
import { storyPropertyQueryKeys } from './storyPropertyQueryKeys';
import {
  STORY_QUERY_ROOT,
  storyQueryScope,
  type StoryQueryKey,
} from "./storyQueryKeyBase";
import type {
  StandBundleKeyArgs,
  StandInspectionMapKeyArgs,
  StandPlanningKeyArgs,
  StandPreflightKeyArgs,
  StandQuotaKeyArgs,
  StandRunsKeyArgs,
  StandScopedKeyArgs,
  StandTapioKeyArgs,
  StandTapioTrajectoryKeyArgs,
  ValuationSnapshotKeyArgs,
} from "./storyQueryKeyTypes";

export type { StoryQueryKey } from "./storyQueryKeyBase";

const scope = storyQueryScope;

const normalized = (values: readonly string[] = []): readonly string[] =>
  Object.freeze([...new Set(values.filter(Boolean))].sort());

export type {
  StandBundleKeyArgs,
  StandInspectionMapKeyArgs,
  StandPlanningKeyArgs,
  StandPreflightKeyArgs,
  StandQuotaKeyArgs,
  StandRunsKeyArgs,
  StandScopedKeyArgs,
  StandTapioKeyArgs,
  StandTapioTrajectoryKeyArgs,
  ValuationSnapshotKeyArgs,
} from "./storyQueryKeyTypes";

export const storyQueryKeys = Object.freeze({
  all: Object.freeze([STORY_QUERY_ROOT]) as StoryQueryKey,
  timberPrices: timberPriceQueryKeys,
  ...storyPropertyQueryKeys,
  stand: Object.freeze({
    all: scope("stand"),
    property: (propertyId: string): StoryQueryKey => scope("stand", propertyId),
    sourceDependentProperty: (propertyId: string): StoryQueryKey =>
      scope("stand", propertyId, "source-dependent"),
    sourceDependentRouteStand: (
      propertyId: string,
      routeStandId: string,
    ): StoryQueryKey =>
      scope("stand", propertyId, "source-dependent", "route", routeStandId),
    sourceDependentResolvedStand: (
      propertyId: string,
      resolvedStandId: string,
    ): StoryQueryKey =>
      scope(
        "stand",
        propertyId,
        "source-dependent",
        "resolved",
        resolvedStandId,
      ),
    routeStand: (propertyId: string, routeStandId: string): StoryQueryKey =>
      scope("stand", propertyId, "route", routeStandId),
    resolvedStand: (
      propertyId: string,
      resolvedStandId: string,
    ): StoryQueryKey => scope("stand", propertyId, "resolved", resolvedStandId),
    bundleScope: (propertyId: string, routeStandId: string): StoryQueryKey => [
      ...storyQueryKeys.stand.sourceDependentRouteStand(
        propertyId,
        routeStandId,
      ),
      "detail-bundle",
    ],
    bundle: (args: StandBundleKeyArgs): StoryQueryKey => [
      ...storyQueryKeys.stand.bundleScope(
        args.propertyId ?? "",
        args.routeStandId ?? "",
      ),
      args.authSessionKey ?? "anonymous",
      args.language ?? "en",
      normalized(args.include),
      args.limit ?? null,
    ],
    planningSupportScope: (
      propertyId: string,
      standId: string,
    ): StoryQueryKey => [
      ...storyQueryKeys.stand.sourceDependentResolvedStand(propertyId, standId),
      "planning-support",
    ],
    planningSupport: (args: StandPlanningKeyArgs): StoryQueryKey => [
      ...storyQueryKeys.stand.planningSupportScope(
        args.propertyId ?? "",
        args.standId ?? "",
      ),
      args.language ?? "en",
      args.priceWindowMonths ?? null,
      args.priceRegion ?? null,
      args.includeValueGrowth === true,
      args.includeBareLandValue === true,
    ],
    preflightScope: (propertyId: string, standId: string): StoryQueryKey => [
      ...storyQueryKeys.stand.sourceDependentResolvedStand(propertyId, standId),
      "simulation-preflight",
    ],
    preflight: (args: StandPreflightKeyArgs): StoryQueryKey => [
      ...storyQueryKeys.stand.preflightScope(
        args.propertyId ?? "",
        args.standId ?? "",
      ),
      args.sourceBasisKey ?? "unresolved-source-basis",
    ],
    quotaScope: (propertyId: string, standId: string): StoryQueryKey => [
      ...storyQueryKeys.stand.sourceDependentResolvedStand(propertyId, standId),
      "access-quota",
    ],
    quota: (args: StandQuotaKeyArgs): StoryQueryKey => [
      ...storyQueryKeys.stand.quotaScope(
        args.propertyId ?? "",
        args.standId ?? "",
      ),
      args.authSessionKey ?? "anonymous",
    ],
    simulationRuns: (args: StandRunsKeyArgs): StoryQueryKey => [
      ...scope("stand", args.propertyId ?? "", "resolved", args.standId ?? ""),
      "simulation-runs",
      args.limit ?? 20,
    ],
    simulationStorage: (args: StandScopedKeyArgs): StoryQueryKey => [
      ...scope("stand", args.propertyId ?? "", "resolved", args.standId ?? ""),
      "simulation-storage",
    ],
    simulationRun: (
      propertyId: string,
      standId: string,
      runId: string,
    ): StoryQueryKey => [
      ...scope("stand", propertyId, "resolved", standId),
      "simulation-run",
      runId,
    ],
    activeBranchEconomics: (
      propertyId: string,
      standId: string,
      runId: string,
      snapshotId: string,
      branchIds: readonly string[] = [],
    ): StoryQueryKey => [
      ...scope("stand", propertyId, "resolved", standId),
      "active-branch-economics",
      runId,
      snapshotId,
      normalized(branchIds).join(","),
    ],
    branchDetail: (
      propertyId: string,
      standId: string,
      runId: string,
      branchId: string,
    ): StoryQueryKey => [
      ...scope("stand", propertyId, "resolved", standId),
      "branch-detail",
      runId,
      branchId,
    ],
    branchComparison: (
      runId: string,
      branchIds: readonly string[],
    ): StoryQueryKey => [
      ...scope("stand", "simulation"),
      "branch-comparison",
      runId,
      normalized(branchIds),
    ],
    branchValueGrowth: (
      runId: string,
      branchId: string,
      snapshotId: string,
    ): StoryQueryKey => [
      ...scope("stand", "simulation"),
      "branch-value-growth",
      runId,
      branchId,
      snapshotId,
    ],
    inspectionMapScope: (
      propertyId: string,
      standId: string,
    ): StoryQueryKey => [
      ...storyQueryKeys.stand.sourceDependentResolvedStand(propertyId, standId),
      "inspection-map",
    ],
    inspectionMap: (args: StandInspectionMapKeyArgs): StoryQueryKey => [
      ...storyQueryKeys.stand.inspectionMapScope(
        args.propertyId ?? "",
        args.standId ?? "",
      ),
      args.measurementSource ?? "resolved",
      args.campaignId ?? "active",
    ],
    inspectionPlots: (propertyId: string, standId: string): StoryQueryKey => [
      ...storyQueryKeys.stand.sourceDependentResolvedStand(propertyId, standId),
      "inspection-plots",
    ],
    nearbyStands: (propertyId: string, standId: string): StoryQueryKey => [
      ...storyQueryKeys.stand.sourceDependentResolvedStand(propertyId, standId),
      "nearby-stands",
    ],
    tapioLimitsScope: (propertyId: string, standId: string): StoryQueryKey => [
      ...storyQueryKeys.stand.sourceDependentResolvedStand(propertyId, standId),
      "tapio-limits",
    ],
    tapioLimits: (args: StandTapioKeyArgs): StoryQueryKey => [
      ...storyQueryKeys.stand.tapioLimitsScope(
        args.propertyId ?? "",
        args.standId ?? "",
      ),
      args.sourceBasisKey ?? "unknown-source",
      args.declaredManagementRegime ?? "undeclared-regime",
      args.corridorSetId ?? "no-corridor-set",
      args.discountRate ?? "no-discount-rate",
    ],
    hazards: (propertyId: string, standId: string): StoryQueryKey => [
      ...storyQueryKeys.stand.sourceDependentResolvedStand(propertyId, standId),
      "hazards",
    ],
    tapioTrajectory: (args: StandTapioTrajectoryKeyArgs): StoryQueryKey => [
      ...storyQueryKeys.stand.sourceDependentResolvedStand(
        args.propertyId ?? "",
        args.standId ?? "",
      ),
      "tapio-trajectory",
      args.runId ?? "no-run",
      args.branchId ?? "no-branch",
      args.corridorSetId ?? "published-rate-sets",
      args.declaredManagementRegime ?? "undeclared-regime",
      args.standSnapshotKey ?? "unknown-stand-snapshot",
    ],
  }),
  measurements: Object.freeze({
    all: scope("measurements"),
    atlas: (): StoryQueryKey => scope("measurements", "atlas"),
    dashboard: (sessionKey?: string, propertyId?: string): StoryQueryKey =>
      scope("measurements", "dashboard", ...(sessionKey ? [sessionKey] : []),
        ...(propertyId ? ["property", propertyId] : [])),
    property: (propertyId: string): StoryQueryKey =>
      scope("measurements", "property", propertyId),
    propertyStudio: (
      propertyId: string,
      selectedStandId: string | null,
      selectedPlotId: string | null,
      comparisonCampaignId: number | null,
      sessionKey?: string,
    ): StoryQueryKey => [
      ...scope(
        "measurements",
        "property",
        propertyId,
        ...(sessionKey ? [sessionKey] : []),
      ),
      "studio",
      selectedStandId ?? "property",
      selectedPlotId ?? "no-plot",
      comparisonCampaignId ?? "active",
    ],
    campaign: (campaignId: number | string): StoryQueryKey =>
      scope("measurements", "campaign", campaignId),
  }),
  inventory: Object.freeze({
    all: scope("inventory"),
    property: (propertyId: string): StoryQueryKey =>
      scope("inventory", propertyId),
    workspace: (propertyId: string): StoryQueryKey =>
      scope("inventory", propertyId, "measurement-workspace"),
    summary: (propertyId: string): StoryQueryKey =>
      scope("inventory", propertyId, "measurement-summary"),
    sourcePreferences: (propertyId: string): StoryQueryKey =>
      scope("inventory", propertyId, "source-preferences"),
    sourceOptions: (propertyId: string): StoryQueryKey =>
      scope("inventory", propertyId, "source-options"),
  }),
  campaigns: Object.freeze({
    all: scope("campaigns"),
    landingAll: scope("campaigns", "landing"),
    property: (propertyId: string): StoryQueryKey =>
      scope("campaigns", propertyId),
    landing: (subscriptionRevision: string): StoryQueryKey =>
      scope("campaigns", "landing", subscriptionRevision),
    list: (propertyId: string): StoryQueryKey =>
      scope("campaigns", propertyId, "list"),
    detail: (propertyId: string, campaignId: number | string): StoryQueryKey =>
      scope("campaigns", propertyId, "detail", campaignId),
    plotDetails: (
      propertyId: string,
      campaignId: number | string,
      revision: string,
    ): StoryQueryKey =>
      scope("campaigns", propertyId, "detail", campaignId, "plots", revision),
  }),
  planning: Object.freeze({
    all: scope("planning"),
    property: (propertyId: string): StoryQueryKey =>
      scope("planning", propertyId),
    snapshot: (
      propertyId: string,
      language: string,
      priceWindowMonths?: number | null,
      priceRegion?: string | null,
    ): StoryQueryKey =>
      scope(
        "planning",
        propertyId,
        "snapshot",
        language,
        priceWindowMonths ?? null,
        priceRegion ?? null,
      ),
    baseline: (
      propertyId: string,
      language: string,
      priceWindowMonths?: number | null,
      priceRegion?: string | null,
    ): StoryQueryKey =>
      scope(
        "planning",
        propertyId,
        "baseline",
        language,
        priceWindowMonths ?? null,
        priceRegion ?? null,
      ),
    structuralBaseline: (propertyId: string): StoryQueryKey =>
      scope("planning", propertyId, "baseline", "structural"),
    savedPlans: (propertyId: string): StoryQueryKey =>
      scope("planning", propertyId, "saved-plans"),
    simulationCatalog: (
      propertyId: string,
      standIds: readonly string[] = [],
    ): StoryQueryKey =>
      scope("planning", propertyId, "simulation-catalog", normalized(standIds)),
    simulationStorage: (
      propertyId: string,
      standIds: readonly string[] = [],
    ): StoryQueryKey =>
      scope("planning", propertyId, "simulation-storage", normalized(standIds)),
    selectedPlan: (
      propertyId: string,
      planId: string,
      payloadRevision?: string,
      revaluationRevision?: string,
    ): StoryQueryKey =>
      scope(
        "planning",
        propertyId,
        "selected-plan",
        planId,
        ...(payloadRevision
          ? ["revision", payloadRevision, revaluationRevision ?? null]
          : []),
      ),
    revaluation: (
      propertyId: string,
      planId: string,
      snapshotId: string,
    ): StoryQueryKey =>
      scope("planning", propertyId, "revaluation", planId, snapshotId),
    planTapioLimits: (
      propertyId: string,
      planId: string,
      standIds: readonly string[] = [],
      selectors: {
        declaredManagementRegime?: string | null;
        corridorSetId?: string | null;
        discountRate?: number | null;
      } = {},
    ): StoryQueryKey =>
      scope(
        "planning",
        propertyId,
        "tapio-limits",
        planId,
        normalized(standIds),
        selectors.declaredManagementRegime ?? "undeclared-regime",
        selectors.corridorSetId ?? "no-corridor-set",
        selectors.discountRate ?? "no-discount-rate",
      ),
  }),
  simulations: Object.freeze({
    all: scope("simulations"),
    property: (propertyId: string): StoryQueryKey =>
      scope("simulations", propertyId),
    branches: (propertyId: string, runId: string): StoryQueryKey =>
      scope("simulations", propertyId, "branches", runId),
  }),
  map: Object.freeze({
    all: scope("map"),
    domain: (propertyId: string): StoryQueryKey =>
      scope("map", propertyId, "domain"),
    workspaceState: scope("map", "workspace-state"),
    property: (propertyId: string): StoryQueryKey => scope("map", propertyId),
    overlays: (propertyId: string): StoryQueryKey =>
      scope("map", propertyId, "overlays"),
    radarTimeline: (propertyId: string, product: string): StoryQueryKey =>
      scope("map", propertyId, "radar", product, "timeline"),
    radarSummary: (
      propertyId: string,
      product: string,
      start: string,
      end: string,
    ): StoryQueryKey =>
      scope("map", propertyId, "radar", product, "summary", start, end),
  }),
  propertyRisk: Object.freeze({
    all: scope("property-risk"),
    property: (propertyId: string): StoryQueryKey =>
      scope("property-risk", propertyId),
    overlay: (propertyId: string, inventoryRevision: string): StoryQueryKey =>
      scope("property-risk", propertyId, "overlay", inventoryRevision),
    // Prefix covering every cached overlay revision for one property, WITHOUT
    // matching sibling `watch` queries
    // that also live under the broader `property(propertyId)` prefix. Use
    // this — never `property(propertyId)` — for entitlement-driven overlay
    // cache purges; `property(propertyId)` stays reserved for deliberate
    // cross-domain invalidation (see storyQueryInvalidation.ts).
    overlayAll: (propertyId: string): StoryQueryKey =>
      scope("property-risk", propertyId, "overlay"),
    watch: (propertyId: string): StoryQueryKey =>
      scope("property-risk", propertyId, "watch"),
    windObservation: (propertyId: string): StoryQueryKey =>
      scope("property-risk", propertyId, "wind-observation"),
    rainfallVersusNormal: (propertyId: string, start = '', end = ''): StoryQueryKey =>
      start && end
        ? scope('property-risk', propertyId, 'rainfall-versus-normal', start, end)
        : scope('property-risk', propertyId, 'rainfall-versus-normal'),
  }),
  valuation: Object.freeze({
    all: scope("valuation"),
    property: (propertyId: string): StoryQueryKey =>
      scope("valuation", propertyId),
    snapshot: (args: ValuationSnapshotKeyArgs): StoryQueryKey =>
      scope(
        "valuation",
        args.propertyId,
        "snapshot",
        args.authSessionKey,
        args.accessLevel,
        args.municipality ?? "unknown-municipality",
        args.priceWindowMonths,
        args.priceRegion ?? "default-region",
        args.sourceBasisKey,
      ),
    opportunities: (
      propertyId: string,
      discountRate: number,
      basalAreaMin: number,
    ): StoryQueryKey =>
      scope(
        "valuation",
        propertyId,
        "opportunities",
        discountRate,
        basalAreaMin,
      ),
  }),
  commerce: Object.freeze({
    all: scope("commerce"),
    property: (propertyId: string): StoryQueryKey =>
      scope("commerce", propertyId),
    offers: (propertyId: string): StoryQueryKey =>
      scope("commerce", propertyId, "offers"),
    tenders: (propertyId: string): StoryQueryKey =>
      scope("commerce", propertyId, "tenders"),
    saleContext: (
      propertyId: string,
      standIds: readonly string[],
    ): StoryQueryKey =>
      scope("commerce", propertyId, "sale-context", normalized(standIds)),
    tenderReport: (
      propertyId: string,
      requestedPlanId: string,
      standIds: readonly string[],
      ownerName: string | null): StoryQueryKey => [
      ...scope("commerce", propertyId, "tenders", "report"),
      requestedPlanId,
      normalized(standIds),
      ownerName ?? "account-name",
    ],
  }),
  package: Object.freeze({
    all: scope("package"),
    account: (sessionKey: string): StoryQueryKey =>
      scope("package", sessionKey),
    pricingTiers: (sessionKey: string): StoryQueryKey =>
      scope("package", sessionKey, "pricing-tiers"),
    publicCatalog: (): StoryQueryKey => scope("package", "catalog", "public"),
    flatSkuCatalog: (): StoryQueryKey =>
      scope("package", "catalog", "flat-skus"),
  }),
  pat: Object.freeze({
    all: scope("pat"),
    account: (sessionKey: string): StoryQueryKey => scope("pat", sessionKey),
  }),
});

export type StoryQueryKeys = typeof storyQueryKeys;
